import { Ban, Camera, MessageSquare, Scale, ShieldCheck, Users } from "lucide-react";
import { useLocale } from "@/lib/i18n/locale-context";

const sections = [
  {
    icon: Ban,
    title: "Kontaktuppgifter hör inte hemma i annonsen",
    points: [
      "Telefonnummer får inte skrivas i rubrik, beskrivning eller synas på bilder – inte heller utskrivet i ord eller med mellanslag.",
      "E-postadresser är inte tillåtna i annonstexten.",
      "Länkar och webbadresser blockeras, även förkortade länkar.",
      "Hänvisningar till Facebook, Instagram, WhatsApp, Telegram, Snapchat, TikTok eller andra plattformar är inte tillåtna.",
      "QR-koder i bilderna blockeras.",
    ],
  },
  {
    icon: Scale,
    title: "Så funkar bud på auktioner",
    points: [
      "Ett bud är ett erbjudande att köpa. Vinner du auktionen förväntas du fullfölja affären.",
      "Lägsta höjning är 5 % av ledande bud, avrundat, och aldrig under 10 kr.",
      "Kommer ett bud in de sista fem minuterna förlängs auktionen fem minuter.",
      "Blir affären inte av med vinnaren kan säljaren gå vidare till nästa budgivare. Det tidigare budet stryks och den nya budgivaren får besked.",
      "Sellavo är en annonsplats, inte auktionsförrättare. Avtalet sluts mellan säljare och köpare.",
    ],
  },
  {
    icon: MessageSquare,
    title: "All kontakt sker via Sellavo",
    points: [
      "Köpare hör av sig via meddelanden på sajten. Du svarar i din inkorg.",
      "Din e-post och ditt telefonnummer visas aldrig publikt – de används bara av oss om vi behöver nå dig.",
      "Bara ditt visningsnamn syns i annonsen. Vi rekommenderar förnamn eller alias.",
    ],
  },
  {
    icon: Camera,
    title: "Bilder",
    points: [
      "Använd egna bilder på den faktiska prylen – inga bilder hämtade från nätet eller andra annonser.",
      "Bilderna ska visa prylen tydligt och inte vara vilseledande.",
      "Inga bilder med stötande, kränkande eller olämpligt innehåll.",
      "Undvik att få med hemadress, registreringsskyltar, dokument eller annat som avslöjar personuppgifter.",
    ],
  },
  {
    icon: Users,
    title: "Bilder där människor syns",
    points: [
      "Det är tillåtet att människor syns på bilden – men bara om alla som går att känna igen har gett sitt samtycke.",
      "Vi granskar bilderna automatiskt. Upptäcker vi en identifierbar person måste du kryssa i ett intyg om att samtycke finns innan annonsen kan publiceras.",
      "Är personen under 18 år krävs samtycke från vårdnadshavare.",
      "Du som annonsör ansvarar fullt ut för att samtycke finns. Sellavo tar inget ansvar för bilder som lagts upp utan samtycke, och vi tar bort annonsen om någon hör av sig.",
      "Enklast är förstås att fota om prylen utan personer i bild.",
    ],
  },
  {
    icon: Scale,
    title: "Prylen och ansvaret",
    points: [
      "Du måste ha rätt att sälja prylen och rätt att använda bilderna.",
      "Olagliga prylar, vapen, läkemedel, djur, stöldgods och kopior är inte tillåtna.",
      "Priset ska vara det du faktiskt säljer för – inga lockpriser.",
      "Sellavo är en annonsplats. Affären görs upp mellan köpare och säljare.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Om reglerna bryts",
    points: [
      "Vi kan dölja, ändra eller radera en annons som bryter mot reglerna.",
      "Vid upprepade eller allvarliga brott kan kontot stängas av.",
      "Alla annonser kan anmälas direkt på annonssidan – vi går igenom varje anmälan.",
    ],
  },
];

const translatedSections = {
  en: [
    ["Contact details do not belong in listings", ["Phone numbers may not appear in the title, description or images, including numbers written as words or with spaces.", "Email addresses are not allowed in listing text.", "Links and web addresses are blocked, including shortened links.", "References to Facebook, Instagram, WhatsApp, Telegram, Snapchat, TikTok or other platforms are not allowed.", "QR codes in images are blocked."]],
    ["How auction bids work", ["A bid is an offer to buy. If you win, you are expected to complete the purchase.", "The minimum increase is 5% of the leading bid, rounded, and never less than SEK 10.", "A bid in the final five minutes extends the auction by five minutes.", "If the winner does not complete the purchase, the seller may approach the next bidder.", "Sellavo is a marketplace, not an auctioneer. The agreement is between seller and buyer."]],
    ["All contact takes place through Sellavo", ["Buyers contact you through messages and you reply in your inbox.", "Your email and phone number are never public.", "Only your display name appears in the listing. We recommend a first name or alias."]],
    ["Images", ["Use your own photos of the actual item, not images from the internet or other listings.", "Images must show the item clearly and must not be misleading.", "Offensive, abusive or inappropriate images are not allowed.", "Avoid showing addresses, licence plates, documents or other personal data."]],
    ["Images showing people", ["People may appear only when everyone who can be recognised has consented.", "We review images automatically. If a person is identifiable, you must confirm consent before publishing.", "A parent or guardian must consent for anyone under 18.", "You are responsible for obtaining consent. We remove the listing if someone contacts us.", "The easiest option is to retake the photo without people in it."]],
    ["The item and your responsibility", ["You must have the right to sell the item and use the images.", "Illegal items, weapons, medicines, animals, stolen goods and counterfeits are not allowed.", "The displayed price must be the real selling price; bait pricing is not allowed.", "Sellavo is a marketplace. The deal is made between buyer and seller."]],
    ["If the rules are broken", ["We may hide, edit or delete a listing that breaks the rules.", "Repeated or serious breaches may lead to account suspension.", "Every listing can be reported from its page, and we review each report."]],
  ],
  es: [
    ["Los datos de contacto no deben aparecer en el anuncio", ["Los números de teléfono no pueden aparecer en el título, la descripción ni las imágenes, tampoco escritos con palabras o espacios.", "No se permiten direcciones de correo electrónico en el texto.", "Se bloquean los enlaces y direcciones web, incluidos los enlaces acortados.", "No se permiten referencias a Facebook, Instagram, WhatsApp, Telegram, Snapchat, TikTok u otras plataformas.", "Se bloquean los códigos QR en las imágenes."]],
    ["Cómo funcionan las pujas", ["Una puja es una oferta de compra. Si ganas, debes completar la operación.", "El incremento mínimo es del 5 % sobre la puja principal, redondeado, y nunca inferior a 10 SEK.", "Una puja durante los últimos cinco minutos amplía la subasta cinco minutos.", "Si el ganador no completa la compra, el vendedor puede acudir al siguiente postor.", "Sellavo es un mercado, no una casa de subastas. El acuerdo se celebra entre comprador y vendedor."]],
    ["Todo el contacto se realiza en Sellavo", ["Los compradores te escriben por mensajes y respondes desde tu bandeja de entrada.", "Tu correo y teléfono nunca son públicos.", "Solo aparece tu nombre visible. Recomendamos usar el nombre de pila o un alias."]],
    ["Imágenes", ["Usa fotos propias del artículo real, no imágenes de internet ni de otros anuncios.", "Las imágenes deben mostrar el artículo claramente y no ser engañosas.", "No se permiten imágenes ofensivas, abusivas o inapropiadas.", "Evita mostrar direcciones, matrículas, documentos u otros datos personales."]],
    ["Imágenes con personas", ["Solo pueden aparecer personas si todas las reconocibles han dado su consentimiento.", "Revisamos las imágenes automáticamente. Si una persona es identificable, debes confirmar el consentimiento antes de publicar.", "Para menores de 18 años se requiere el consentimiento de su tutor.", "Tú eres responsable de obtener el consentimiento. Retiraremos el anuncio si alguien nos contacta.", "Lo más sencillo es repetir la foto sin personas."]],
    ["El artículo y tu responsabilidad", ["Debes tener derecho a vender el artículo y utilizar las imágenes.", "No se permiten artículos ilegales, armas, medicamentos, animales, bienes robados ni falsificaciones.", "El precio mostrado debe ser el precio real de venta; no se permiten precios gancho.", "Sellavo es un mercado. La operación se acuerda entre comprador y vendedor."]],
    ["Si se incumplen las reglas", ["Podemos ocultar, editar o eliminar un anuncio que incumpla las reglas.", "Las infracciones repetidas o graves pueden causar la suspensión de la cuenta.", "Todos los anuncios se pueden denunciar desde su página y revisamos cada denuncia."]],
  ],
} as const;

export function AdRulesContent({ compact = false }: { compact?: boolean }) {
  const { lang } = useLocale();
  const displaySections = lang === "sv" ? sections : sections.map((section, index) => ({
    ...section,
    title: translatedSections[lang][index][0],
    points: [...translatedSections[lang][index][1]],
  }));
  return (
    <div className={compact ? "grid gap-4" : "grid gap-5"}>
      {displaySections.map((section) => (
        <article key={section.title} className="rounded-md border border-border bg-card p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-ocean-300/15">
              <section.icon className="h-4.5 w-4.5 text-ocean-500" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h2 className="font-heading text-lg font-semibold text-foreground">{section.title}</h2>
              <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
                {section.points.map((point) => (
                  <li key={point} className="flex gap-2">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-ocean-400" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}